import { Transaccion, Categoria, Cuenta } from '../types';
import { guardarArchivo } from './plataforma';

// ── EXPORTAR ─────────────────────────────────────────────────────────────────

export function exportarTransaccionesCSV(
  transacciones: Transaccion[],
  categorias: Categoria[],
  cuentas: Cuenta[]
): void {
  const getCat = (t: Transaccion) => {
    if (t.traspasoId) return 'Traspaso';
    if (t.adelanto) return 'Adelanto';
    return categorias.find(c => c.id === t.categoriaId)?.nombre || t.categoriaId;
  };
  const getTipo = (t: Transaccion) => {
    if (t.traspasoId) return t.tipo === 'ingreso' ? 'Entrada' : 'Salida';
    return t.tipo === 'ingreso' ? 'Ingreso' : 'Gasto';
  };
  // Gastos/Salidas negativos, formato europeo con coma (##0,00) para Excel
  const getImporte = (t: Transaccion) => {
    const signo = (t.tipo === 'gasto') ? '-' : '';
    return `${signo}${t.cantidad.toFixed(2).replace('.', ',')}`;
  };
  const getCuenta = (id: string) => cuentas.find(c => c.id === id)?.nombre || id;

  const cabecera = ['Fecha', 'Tipo', 'Categoria', 'Cuenta', 'Descripcion', 'Importe', 'Persona'];

  const filas = [...transacciones]
    .sort((a, b) => (b.secuencia ?? 0) - (a.secuencia ?? 0))
    .map(t => [
      new Date(t.fecha).toISOString().split('T')[0],
      getTipo(t),
      getCat(t),
      getCuenta(t.cuentaId),
      `"${(t.descripcion || '').replace(/"/g, '""')}"`,
      getImporte(t),
      t.adelanto ? `"${(t.persona || '').replace(/"/g, '""')}"` : '',
    ]);

  const contenido = [cabecera, ...filas].map(r => r.join(';')).join('\n');
  const bom = '﻿'; // BOM para que Excel reconozca UTF-8
  const blob = new Blob([bom + contenido], { type: 'text/csv;charset=utf-8;' });

  guardarArchivo(`transacciones_${new Date().toISOString().slice(0, 10)}.csv`, blob)
    .catch(e => console.error('No se pudo exportar el CSV', e));
}

// ── IMPORTAR ─────────────────────────────────────────────────────────────────

export interface FilaCSV {
  fecha: string;
  tipo: 'ingreso' | 'gasto';
  categoriaNombre: string;
  cuentaNombre: string;
  descripcion: string;
  cantidad: number;
  persona?: string;       // solo adelantos
}

export function parsearCSV(texto: string): { filas: FilaCSV[]; errores: string[] } {
  const lineas = texto.trim().split(/\r?\n/);
  const errores: string[] = [];
  const filas: FilaCSV[] = [];

  if (lineas.length < 2) {
    return { filas, errores: ['El fichero no contiene datos'] };
  }

  // Detectar separador (coma o punto y coma)
  const sep = lineas[0].includes(';') ? ';' : ',';
  const cabecera = lineas[0].toLowerCase().split(sep).map(h => h.trim().replace(/"/g, ''));

  const idx = {
    fecha: cabecera.findIndex(h => h === 'fecha'),
    tipo: cabecera.findIndex(h => h === 'tipo'),
    categoria: cabecera.findIndex(h => ['categoria', 'categoría'].includes(h)),
    cuenta: cabecera.findIndex(h => h === 'cuenta'),
    descripcion: cabecera.findIndex(h => ['descripcion', 'descripción'].includes(h)),
    cantidad: cabecera.findIndex(h => h === 'cantidad' || h === 'importe'),
    persona: cabecera.findIndex(h => h === 'persona'),
  };

  if (idx.fecha < 0 || idx.tipo < 0 || idx.categoria < 0 || idx.cuenta < 0 || idx.cantidad < 0) {
    return {
      filas,
      errores: ['Cabeceras incorrectas. Se esperan: Fecha;Tipo;Categoria;Cuenta;Descripcion;Importe'],
    };
  }

  for (let i = 1; i < lineas.length; i++) {
    const linea = lineas[i].trim();
    if (!linea) continue;

    // Separar respetando comillas dobles
    const cols = linea.split(sep).map(c => c.trim().replace(/^"|"$/g, '').replace(/""/g, '"'));

    const tipo = cols[idx.tipo]?.toLowerCase() as 'ingreso' | 'gasto';
    if (tipo !== 'ingreso' && tipo !== 'gasto') {
      errores.push(`Línea ${i + 1}: tipo '${cols[idx.tipo]}' no válido (debe ser ingreso o gasto)`);
      continue;
    }

    const cantidad = parseFloat(cols[idx.cantidad]?.replace(',', '.'));
    if (isNaN(cantidad) || cantidad <= 0) {
      errores.push(`Línea ${i + 1}: cantidad '${cols[idx.cantidad]}' no válida`);
      continue;
    }

    const fecha = cols[idx.fecha];
    if (!fecha || isNaN(Date.parse(fecha))) {
      errores.push(`Línea ${i + 1}: fecha '${fecha}' no válida (usar formato YYYY-MM-DD)`);
      continue;
    }

    filas.push({
      fecha,
      tipo,
      categoriaNombre: cols[idx.categoria] || '',
      cuentaNombre: cols[idx.cuenta] || '',
      descripcion: idx.descripcion >= 0 ? cols[idx.descripcion] || '' : '',
      cantidad,
      persona: idx.persona >= 0 ? cols[idx.persona] || undefined : undefined,
    });
  }

  return { filas, errores };
}
